import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Link, Paper, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { Phone, Check } from "@mui/icons-material";
import {
  OtpInput,
  StepActions,
  PageHeader,
  InfoBanner,
  Callout,
  EditPhoneDialog,
  EditPhoneValues,
} from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setAccountUserPhone, setPersonalData } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useValidatePhoneCodeMutation, useRequestPhoneCodeMutation, usePatchUserMutation } from "@api/onboarding";
import { usePostValidateNextStageMutation } from "@api/applications";
import { usePatchCuentaMutation } from "@api/account";
import { ApiError } from "@interfaces/response";
import { formatTime, formatPhoneNumber } from "@helpers/funciones";
import { EMAIL_CONTACTO, PATHS_TOFU_V2, STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

const TIME_LIMIT = 120;
const OTP_LENGTH = 4;

const OTP_YA_VERIFICADO = "0".repeat(OTP_LENGTH);

/** Grises del spec para la ayuda bajo el código (`--ld-text-2` y `--ld-text-3`). */
const HELP_TEXT = "#4A5567";
const HELP_META = "#7A8497";

export const ValidacionTelefono = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.validacion_telefono.code);

  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const application = useSelector((state: RootState) => state.register.application);

  const [validatePhoneCode] = useValidatePhoneCodeMutation();
  const [requestPhoneCode] = useRequestPhoneCodeMutation();
  const [patchAccount] = usePatchCuentaMutation();
  const [patchUser] = usePatchUserMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [justResent, setJustResent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [openEditPhone, setOpenEditPhone] = useState(false);

  const otpListo = otp.length === 4 || clientDetails.cell_phone_verified;

  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const t = setTimeout(() => setSecondsRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsRemaining]);

  const handleResendCode = async () => {
    setResending(true);
    setRateLimited(false);
    try {
      await requestPhoneCode({ country_code: accountUser.code_phone, phone_number: accountUser.phone }).unwrap();
      setSecondsRemaining(TIME_LIMIT);
      setJustResent(true);
      setTimeout(() => setJustResent(false), 4000);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) setRateLimited(true);
    } finally {
      setResending(false);
    }
  };

  const handleSavePhone = async ({ code_phone_number: country_code, phone_number: phone }: EditPhoneValues) => {
    // El legacy (ModalPhone) cierra sin llamar al API cuando el teléfono no cambió.
    if (accountUser.code_phone === country_code && accountUser.phone === phone) {
      setOpenEditPhone(false);
      return;
    }

    setRateLimited(false);
    try {
      await patchUser({
        id: String(accountUser.id),
        data: { phone: `${country_code}${phone}` },
      }).unwrap();

      await requestPhoneCode({ country_code, phone_number: phone }).unwrap();

      dispatch(
        setAccountUserPhone({
          id: accountUser.id,
          phone_full: `${country_code}${phone}`,
          code_phone: country_code,
          phone,
        }),
      );

      setOpenEditPhone(false);
      setSecondsRemaining(TIME_LIMIT);
      setOtp("");
      setErrorMessage("");
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) {
        setRateLimited(true);
        setOpenEditPhone(false);
        return;
      }
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
      Sentry.captureException(apiError, { extra: { component: "ValidacionTelefono - Editar teléfono" } });
    }
  };

  const handleContinue = async () => {
    setErrorMessage("");
    if (!otpListo) return;

    setSubmitting(true);
    try {
      if (!clientDetails.cell_phone_verified) {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        await validatePhoneCode({
          country_code: accountUser.code_phone,
          phone_number: accountUser.phone,
          code: otp,
        }).unwrap();

        await patchAccount({ accountId: clientDetails.id, body: { cell_phone_verified: true } }).unwrap();

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setPersonalData({ ...clientDetails, cell_phone_verified: true }));
      }

      navigate(nextStatus?.url || "");
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 404 || apiError?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        return;
      }
      Sentry.captureException(apiError, { extra: { component: "ValidacionTelefono", otp } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Verificación"
        title="Verifica tu teléfono"
        subtitle="Enviamos un código de 4 dígitos por SMS y WhatsApp."
      />

      <Box sx={{ mb: 3 }}>
        <InfoBanner
          icon={<Phone fontSize="small" />}
          label="Teléfono celular"
          value={
            accountUser.phone ? `(${accountUser.code_phone}) ${formatPhoneNumber(String(accountUser.phone))}` : "—"
          }
          editLabel="Cambiar"
          onEdit={() => setOpenEditPhone(true)}
          editDisabled={clientDetails.cell_phone_verified || submitting}
        />
      </Box>

      <Paper
        variant="lendia_paper"
        sx={{
          textAlign: "center",
          p: "36px 28px 28px",
          boxShadow: "0 1px 2px rgba(14, 31, 58, 0.06), 0 1px 3px rgba(14, 31, 58, 0.04)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <OtpInput
            length={OTP_LENGTH}
            value={clientDetails.cell_phone_verified ? OTP_YA_VERIFICADO : otp}
            onChange={setOtp}
            size="lg"
            disabled={clientDetails.cell_phone_verified || submitting}
          />
        </Box>

        {clientDetails.cell_phone_verified && (
          <Typography
            sx={{
              mt: 2,
              fontSize: 13,
              fontWeight: 500,
              color: "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Check sx={{ fontSize: 16 }} /> Tu teléfono ya fue verificado
          </Typography>
        )}

        {errorMessage && (
          <Box sx={{ mt: 2 }}>
            <Callout tone="warn">{errorMessage}</Callout>
          </Box>
        )}

        {rateLimited && (
          <Box sx={{ mt: 2 }}>
            <Callout tone="warn">
              Excedió el número de intentos permitidos para ese número, intenta más tarde o contacta a un asesor.
            </Callout>
          </Box>
        )}

        {/* Si el teléfono ya está verificado no hay código que reenviar ni que expire. */}
        {/* `.ld-verify-help` — 13px en `--ld-text-2`, notas en 11.5px `--ld-text-3` */}
        {!clientDetails.cell_phone_verified && (
          <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography component="div" sx={{ fontSize: 13, color: HELP_TEXT }}>
              ¿No recibiste el código?{" "}
              {justResent ? (
                <Typography
                  component="span"
                  sx={{
                    fontSize: "inherit",
                    color: "success.main",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Check sx={{ fontSize: 14 }} /> Código reenviado
                </Typography>
              ) : (
                /* `.ld-verify-link` — enlace en negritas del color de marca, sin subrayado */
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={handleResendCode}
                  disabled={secondsRemaining > 0 || resending || submitting}
                  data-testid="validacion-telefono-reenviar-link"
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    fontWeight: 600,
                    color: "primary.main",
                    verticalAlign: "baseline",
                    "&:disabled": { color: HELP_META, cursor: "default", textDecoration: "none" },
                  }}
                >
                  {secondsRemaining > 0 ? `Reenviar en ${formatTime(secondsRemaining)}` : "Reenviar código"}
                </Link>
              )}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: HELP_META }}>El código expira en 5 minutos.</Typography>
            <Typography sx={{ fontSize: 11.5, color: HELP_META, mt: 0.5 }}>
              Si nunca llega el código, escríbenos a{" "}
              <Link
                href={`mailto:${EMAIL_CONTACTO}`}
                underline="hover"
                data-testid="validacion-telefono-contacto-link"
                sx={{ fontSize: "inherit", fontWeight: 500, color: "primary.main" }}
              >
                {EMAIL_CONTACTO}
              </Link>{" "}
              y te ayudamos a validarlo.
            </Typography>
          </Box>
        )}
      </Paper>

      <StepActions
        // Regreso secuencial según `status_config`; la ruta fija queda de respaldo.
        onBack={() => navigate(previousStatus?.url || PATHS_TOFU_V2.registroDatosPersonales.path)}
        onNext={handleContinue}
        nextDisabled={!otpListo || submitting}
      />

      <EditPhoneDialog
        open={openEditPhone}
        initialValues={{
          code_phone_number: accountUser.code_phone || "+52",
          phone_number: accountUser.phone || "",
        }}
        onSubmit={handleSavePhone}
        onClose={() => setOpenEditPhone(false)}
        disabled={clientDetails.cell_phone_verified}
        title="Actualizar número de teléfono"
        hint="A este número enviaremos el código de verificación."
      />
    </Box>
  );
};
